import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface ComingSoonScreenProps {
  countryName: string | null;
}

const ComingSoonScreen: React.FC<ComingSoonScreenProps> = ({ countryName }) => {
  const { t } = useTranslation();
  
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Extract userId from URL if present (e.g., /kyc/personal/:userId/:kycToken)
  const match = window.location.pathname.match(/\/kyc\/(?:personal|entity)\/([^/]+)/);
  const userId = match ? match[1] : null;

  const handleJoinWaitlist = async () => {
    setStatus('loading');
    setErrorMessage('');

    try {
      const apiUrl = process.env.REACT_APP_NODE_API_BASE_URL || 'http://localhost:3050/api/v1';
      const response = await fetch(`${apiUrl}/wso2/sendComingSoonEmail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, countryName: countryName || 'your region' }),
      });

      if (!response.ok) {
        throw new Error('Failed to join waitlist');
      }

      setStatus('success');
    } catch (err) {
      console.error(err);
      setStatus('error');
      setErrorMessage(t('geo.waitlist_error', { defaultValue: 'Something went wrong. Please try again later.' }));
    }
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        backgroundColor: 'rgba(10, 14, 20, 0.97)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        textAlign: 'center',
        padding: '32px 24px',
      }}
    >
      <div style={{ fontSize: 64, marginBottom: 24, lineHeight: 1 }}>⏳</div>
      <h2 style={{ fontWeight: 700, fontSize: '1.6rem', marginBottom: 12, color: '#fff' }}>
        {t('geo.coming_soon_screen_title', { defaultValue: 'Identity Verification Paused' })}
      </h2>
      <p style={{ fontSize: '1rem', color: '#b0bec5', maxWidth: 480, lineHeight: 1.7, marginBottom: 24 }}>
        {t('geo.coming_soon_screen_body', {
          country: countryName ?? t('geo.your_region', { defaultValue: 'your region' }),
          defaultValue:
            'You can create an account with Arm Financial, but full Identity Verification (KYC) is not yet available in {{country}}. We are actively expanding and will notify you when you can complete your profile.',
        })}
      </p>

      {/* Waitlist Form */}
      {status === 'success' ? (
        <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid #10b981', borderRadius: 8, padding: '16px 24px', marginBottom: 32, maxWidth: 400 }}>
          <p style={{ margin: 0, color: '#34d399', fontWeight: 600 }}>🎉 You're on the list!</p>
          <p style={{ margin: '8px 0 0', fontSize: '0.9rem', color: '#a7f3d0' }}>We've sent a confirmation to your registered email address. You'll be the first to know when we launch in {countryName || 'your region'}.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 32, width: '100%', maxWidth: 360 }}>
          <button 
            onClick={handleJoinWaitlist}
            disabled={status === 'loading'}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 8,
              border: 'none',
              background: status === 'loading' ? '#0ea5e9' : '#38bdf8',
              color: '#000',
              fontWeight: 600,
              fontSize: '1rem',
              cursor: status === 'loading' ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
              opacity: status === 'loading' ? 0.7 : 1
            }}
          >
            {status === 'loading' ? 'Joining...' : 'Join the Founding Members Waitlist'}
          </button>
          {status === 'error' && <p style={{ color: '#ef4444', fontSize: '0.85rem', margin: 0 }}>{errorMessage}</p>}
        </div>
      )}

      <Link 
        to="/global-availability"
        style={{ color: '#38bdf8', textDecoration: 'none', marginBottom: 32, fontSize: '1rem' }}
      >
        View full global availability list &rarr;
      </Link>
    </div>
  );
};

export default ComingSoonScreen;
